import { forwardRef, type ReactNode } from 'react'

interface MarqueeTableCardProps {
	/** Fixed header row (title / tabs / counts) that stays put while scrolling. */
	header?: ReactNode
	/** Table content; scrolls inside the bounded slot on desktop. */
	children: ReactNode
}

/**
 * Shared card shell for the marquee list tabs (attendees / check-in / export):
 * a bordered card fixed at 60dvh on desktop, with a fixed header and a
 * bounded, internally-scrolling table slot. Centralizes the card + height +
 * scroll layout so it stays consistent across the three tabs (change it here
 * and all three follow).
 */
export const MarqueeTableCard = forwardRef<
	HTMLDivElement,
	MarqueeTableCardProps
>(function MarqueeTableCard({ header, children }, ref) {
	return (
		<div
			// One height in every state, rather than `h` while loading and
			// `max-h` after. With a max the card tracked its content, so a
			// filter that emptied the list collapsed it to the empty state's
			// ~150px and refilling it sprang back to 60dvh: a ~400px swing in
			// document height that moves the page under the pointer. The
			// loading state was already this tall, so nothing new appears.
			className='flex l:h-[60dvh] flex-col gap-5 l:rounded-airbnb-2xl l:border l:border-grey-200 l:bg-neutral-white l:p-6 l:shadow-airbnb-3'
			ref={ref}
		>
			{header}
			<div className='l:flex l:min-h-0 l:flex-1 l:flex-col'>
				{children}
			</div>
		</div>
	)
})

MarqueeTableCard.displayName = 'MarqueeTableCard'
